import { useState, useEffect, useMemo } from "react";

import {
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  CurrencyPoundIcon,
} from "@heroicons/react/24/outline";

import useBaskets from "../../data/useBaskets";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import { IBasketItem } from "../../models/Basket";
import { IPart } from "../../models/Part";
import { FormatType } from "../../models/EbayOffer";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import Tooltip from "../shared/Tooltip";

type RrpSource = "Market" | "PartPro";

function resolveRrp(part: IPart): { price: number; source: RrpSource } | null {
  const candidates: { price: number | null | undefined; source: RrpSource }[] = [
    { price: part.marketDataPrice, source: "Market" },
    { price: part.suggestedPrice, source: "PartPro" },
  ];
  for (const c of candidates) {
    const n = Number(c.price);
    if (Number.isFinite(n) && n > 0) return { price: n, source: c.source };
  }
  return null;
}

function resolveLastSold(part: IPart): number | null {
  const n = Number(part.lastSoldPrice);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function buildEbaySearchUrl(part: IPart): string {
  // Always link to an eBay search for currently active listings so the user
  // can compare live prices, even if this part already has its own listing.
  const terms = [
    part.partNumber,
    part.oePartNumber,
    part.category?.name,
    part.vehicle?.combinedMake,
    part.vehicle?.combinedModel,
  ]
    .map((t) => (t ?? "").toString().trim())
    .filter(Boolean);
  const query = terms.length ? terms.join(" ") : (part.title ?? "");
  // _sop=15 = Price + Shipping: lowest first, gives a quick read on cheapest
  // active competition. LH_Sold=0 / LH_Complete=0 keeps it to active listings.
  const params = new URLSearchParams({
    _nkw: query,
    _sop: "15",
    LH_Sold: "0",
    LH_Complete: "0",
  });
  return `https://www.ebay.co.uk/sch/i.html?${params.toString()}`;
}

export default function BasketPriceEditor({ basketItem }: { basketItem: IBasketItem }) {
  const { updateItem } = useBaskets("BasketPriceEditor");
  const { saveHandlers } = useDefaultCRUDHandlers("Basket Item");
  const { addNotification } = useNotification();
  const [editPrice, setEditPrice] = useState<string>("");

  const getDefaultPrice = () => {
    if (!basketItem.part.offers || basketItem.part.offers.length === 0) {
      return basketItem.part.enteredPrice || 0;
    }

    const offer = basketItem.part.offers[0];
    if (offer.format === FormatType.FixedPrice) {
      return offer.buyItNowPrice || 0;
    } else {
      return offer.auctionStartPrice || 0;
    }
  };

  const getCurrentPrice = () => {
    return basketItem.basketPrice ?? getDefaultPrice();
  };

  useEffect(() => {
    setEditPrice(getCurrentPrice().toString());
  }, [basketItem]);

  const handlePriceUpdate = (priceOverride?: number) => {
    const newPrice =
      priceOverride !== undefined ? priceOverride : parseFloat(editPrice);

    if (isNaN(newPrice) || newPrice < 0) {
      addNotification({
        primaryText: "Invalid price entered",
        variant: "warn",
      });
      setEditPrice(getCurrentPrice().toString());
      return;
    }

    if (priceOverride !== undefined) {
      setEditPrice(newPrice.toString());
    }

    updateItem.mutate(
      {
        ...basketItem,
        basketPrice: newPrice,
      },
      saveHandlers
    );
  };

  const rrp = useMemo(() => resolveRrp(basketItem.part), [basketItem.part]);
  const lastSold = useMemo(
    () => resolveLastSold(basketItem.part),
    [basketItem.part]
  );
  const hasAnySuggestion = !!rrp || lastSold != null;
  const ebaySearchUrl = useMemo(
    () => buildEbaySearchUrl(basketItem.part),
    [basketItem.part]
  );

  return (
    <div
      className="flex flex-col items-center gap-y-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-x-1.5">
        <span>£</span>
        <input
          value={editPrice}
          onChange={(e) => setEditPrice(e.target.value)}
          onBlur={() => handlePriceUpdate()}
          step={0.01}
          className="h-8 w-20 border p-1 text-sm"
          type="number"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handlePriceUpdate();
            } else if (e.key === "Escape") {
              setEditPrice(getCurrentPrice().toString());
            }
          }}
        />
        <Tooltip content="Search active eBay listings for this part">
          <button
            type="button"
            onClick={() => openInNewTab(ebaySearchUrl)}
            aria-label="Search active eBay listings for this part"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 ring-1 ring-slate-300 transition-colors hover:bg-slate-50 hover:text-primary-600 hover:ring-primary-400"
          >
            <ArrowTopRightOnSquareIcon
              className="h-3.5 w-3.5"
              aria-hidden="true"
            />
          </button>
        </Tooltip>
      </div>

      {hasAnySuggestion ? (
        <div className="flex flex-col items-stretch gap-0.5">
          {rrp && (
            <Tooltip
              content={`Apply ${rrp.source} price (${PriceFormat(rrp.price)})`}
            >
              <button
                type="button"
                onClick={() => handlePriceUpdate(rrp.price)}
                className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-primary-700"
              >
                <CurrencyPoundIcon className="h-3 w-3" aria-hidden="true" />
                <span>RRP {PriceFormat(rrp.price)}</span>
                <span className="rounded bg-slate-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {rrp.source}
                </span>
              </button>
            </Tooltip>
          )}
          {lastSold != null && (
            <Tooltip
              content={`Apply last sold price (${PriceFormat(lastSold)})`}
            >
              <button
                type="button"
                onClick={() => handlePriceUpdate(lastSold)}
                className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-primary-700"
              >
                <ClockIcon className="h-3 w-3" aria-hidden="true" />
                <span>Last Sold {PriceFormat(lastSold)}</span>
              </button>
            </Tooltip>
          )}
        </div>
      ) : (
        <span className="text-[11px] italic text-slate-400">
          No price data
        </span>
      )}
    </div>
  );
}
